<template>
    <div class="pizarra">
        <div v-if="loading" class="text-center">
            <v-progress-circular class="mt-auto" :size="70" :width="7" color="primary"
                indeterminate></v-progress-circular>
        </div>
        <div v-else class="chart-wrapper">

            <div class="dates">
                <v-menu v-model="menu1" :close-on-content-click="false" transition="scale-transition" color="secondary"
                    offset-y max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="formData.yearDate" :label="'*' + $t('report.label.yearDate')"
                            color="secondary" :rules="yearDateRules" readonly v-bind="attrs" v-on="on" :clearable="!!formData.yearDate"></v-text-field>
                    </template>
                    <v-list max-height="300px" class="scrollable-list">
                        <v-list-item-group v-model="selectedYear" @change="handleYearSelection">
                            <v-list-item
                                v-for="year in availableYears"
                                :key="year"
                                :value="year"
                                class="year-list-item">
                                <v-list-item-title>{{ year }}</v-list-item-title>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-menu>
    
                <v-btn 
                    rounded 
                    color="primary"
                    :disabled="!formData.yearDate"
                    @click="loadChartData()">
                    {{ $t('button.consult') }}
                </v-btn>
            </div>

            <!-- Gráfica W1 (Mañana) -->
            <div class="chart-container col-md-9 col-sm-12">
                <h2>{{ $t('label.chartMorning') }}</h2>
                <div v-if="hasDataW1">
                    <line-chart 
                        :chart-data="chartDataW1" 
                        :options="baseChartOptions"
                    />
                </div>
                <div v-else class="no-data-message">
                    <p>{{ $t('label.dataMorning') }}</p>
                </div>
            </div>

            <!-- Gráfica W2 (Tarde) -->
            <div class="chart-container col-md-9 col-sm-12">
                <h2>{{ $t('label.chartAfternoon') }}</h2>
                <div v-if="hasDataW2">
                    <line-chart 
                        :chart-data="chartDataW2" 
                        :options="baseChartOptions"
                    />
                </div>
                <div v-else class="no-data-message">
                    <p>{{ $t('label.dataAfternoon') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
import { Line } from 'vue-chartjs';

// Componente de gráfica de líneas personalizado
const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartData, this.options)
      }
    }
  }
}

import {
    HAPPINESS_M,
    ACTION_GET_RESULTS_ANNUAL_CHART,
    GET_RESULTS_ANNUAL_CHART,
    GET_STATE_LOADING
} from "@/views/chart/store/types/types.js";
import { mapActions, mapGetters } from "vuex";
import i18n from '@/plugins/i18n';
import Swal from "sweetalert2";

export default {
    name: "annualLineChartComponent",
    components: {
        LineChart
    },
    data() {
        return {
            formData: {
                yearDate: null,
            },
            yearDateRules: [
                v => !!v || this.$t('report.label.yearDate.required')
            ],
            menu1: false, // Para el menú de selección de año
            selectedYear: null, // Para v-list-item-group

            resultsAnnual: null,
            windowsData: {
                W1: {
                    labels: [],
                    datasets: []
                },
                W2: {
                    labels: [],
                    datasets: []
                }
            },
            baseChartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        align: 'center',
                        labels: {
                            boxWidth: 15,
                            padding: 20,
                            color: '#333',
                            font: {
                                size: 12,
                                weight: '500'
                            },
                            usePointStyle: true
                        }
                    }
                },
                scales: {
                    y: {
                        display: true,
                        title: {
                            display: true,
                            color: '#2D0049',
                            font: {
                                size: 14,
                                weight: 'bold',
                                family: 'Arial, sans-serif'
                            },
                            padding: {
                                top: 10,
                                bottom: 10
                            }
                        },
                        beginAtZero: true,
                        grid: {
                            display: true,
                            color: '#e0e0e0',
                            borderDash: [2, 2]
                        },
                        ticks: {
                            color: '#666',
                            font: {
                                size: 11
                            }
                        }
                    },
                    x: {
                        display: true,
                        title: {
                            display: true,
                            color: '#2D0049',
                            font: {
                                size: 14,
                                weight: 'bold',
                                family: 'Arial, sans-serif'
                            },
                            padding: {
                                top: 10,
                                bottom: 5
                            }
                        },
                        grid: {
                            display: false
                        },
                        ticks: {
                            color: '#666',
                            font: {
                                size: 11
                            }
                        }
                    }
                },
                elements: {
                    point: {
                        radius: 4,
                        hoverRadius: 6,
                        borderWidth: 2
                    },
                    line: {
                        tension: 0.4,
                        borderWidth: 3
                    }
                },
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                animation: {
                    duration: 1000,
                    easing: 'easeOutQuart'
                }
            },
        }
    },
    
    async mounted() {
        await this.loadChartData()
    },
    
    methods: {
        handleYearSelection(selectedYear) {
            if (!selectedYear) return;
            
            // Validar que el año no sea anterior a 2025
            const minYear = 2025;
            
            if (selectedYear < minYear) {
                Swal.fire({
                    icon: "error",
                    title: i18n.t('label.date.invalid'),
                    text: i18n.t('label.startDate.invalid.text7')
                });
                this.selectedYear = null;
                this.formData.yearDate = null;
                return;
            }

            this.formData.yearDate = selectedYear;
            this.menu1 = false;
        },

        async loadChartData() {
            try {
                // Si no hay año seleccionado, cargar el año presente
                if (!this.formData.yearDate) {
                    const currentYear = new Date().getFullYear();
                    
                    this.formData.yearDate = currentYear;
                    this.selectedYear = currentYear;
                }
                
                this.resultsAnnual = await this.actionGetresultsAnnualChart(this.formData)
                // Procesar datos del servicio
                this.processChartData()
            } catch (err) {
                console.error("Error cargando datos:", err)
            }
        },

        // Método para procesar datos dinámicos del servicio
        processChartData() {
            if (this.resultsAnnual && 
                this.resultsAnnual.data && 
                this.resultsAnnual.data.payload && 
                this.resultsAnnual.data.payload.windows) {
                const windows = this.resultsAnnual.data.payload.windows;
                // Procesar W1 (Mañana)
                if (windows.W1) {
                    this.windowsData.W1 = {
                        labels: windows.W1.labels || [],
                        datasets: windows.W1.datasets || []
                    };
                }
                
                // Procesar W2 (Tarde)
                if (windows.W2) {
                    this.windowsData.W2 = {
                        labels: windows.W2.labels || [],
                        datasets: windows.W2.datasets || []
                    };
                }
            }
        },

        // Obtener colores para cada dataset
        getDatasetColors(label) {
            const colorMap = {
                [i18n.t('label.chartHappiness')]: {
                    backgroundColor: 'rgba(28, 185, 16, 0.1)',
                    borderColor: '#1cb910',
                    pointBackgroundColor: '#1cb910'
                },
                [i18n.t('label.chartSadness')]: {
                    backgroundColor: 'rgba(185, 16, 16, 0.1)',
                    borderColor: '#b91010',
                    pointBackgroundColor: '#b91010'
                },
                [i18n.t('label.chartNeutral')]: {
                    backgroundColor: 'rgba(185, 181, 16, 0.1)',
                    borderColor: '#b9b510',
                    pointBackgroundColor: '#b9b510'
                }
            };
            
            return colorMap[label] || {
                backgroundColor: 'rgba(128, 128, 128, 0.1)',
                borderColor: '#b9b510',
                pointBackgroundColor: '#b9b510'
            };
        },

        // Método para verificar si hay datos válidos en un dataset
        hasValidData(windowData) {
            if (!windowData.labels || !windowData.datasets) {
                return false;
            }
            
            if (windowData.labels.length === 0 || windowData.datasets.length === 0) {
                return false;
            }
            
            // Verificar si al menos un dataset tiene datos válidos
            return windowData.datasets.some(dataset => 
                dataset.data && 
                dataset.data.length > 0 && 
                dataset.data.some(value => value !== null && value !== undefined && value !== 0)
            );
        },

        ...mapActions({
            actionGetresultsAnnualChart: `${HAPPINESS_M}${ACTION_GET_RESULTS_ANNUAL_CHART}`,
        }),
    },
    
    computed: {
        // Generar lista de años disponibles desde 2025 hasta el año actual
        availableYears() {
            const currentYear = new Date().getFullYear();
            const startYear = 2025;
            const years = [];
            
            for (let year = currentYear; year >= startYear; year--) {
                years.push(year);
            }
            
            return years;
        },

        // Verificar si W1 (Mañana) tiene datos
        hasDataW1() {
            return this.hasValidData(this.windowsData.W1);
        },

        // Verificar si W2 (Tarde) tiene datos
        hasDataW2() {
            return this.hasValidData(this.windowsData.W2);
        },

        // Datos de W1 (Mañana)
        chartDataW1() {
            const windowData = this.windowsData.W1;
            if (!windowData.labels.length || !windowData.datasets.length) {
                return { labels: [], datasets: [] };
            }
            
            const processedDatasets = windowData.datasets.map(dataset => {
                const colors = this.getDatasetColors(dataset.label);
                
                return {
                    label: dataset.label,
                    data: dataset.data,
                    backgroundColor: colors.backgroundColor,
                    borderColor: colors.borderColor,
                    pointBackgroundColor: colors.pointBackgroundColor,
                    pointBorderColor: '#ffffff',
                    fill: true
                };
            });
            
            return {
                labels: windowData.labels,
                datasets: processedDatasets
            };
        },

        // Datos de W2 (Tarde)
        chartDataW2() {
            const windowData = this.windowsData.W2;
            if (!windowData.labels.length || !windowData.datasets.length) {
                return { labels: [], datasets: [] };
            }
            
            const processedDatasets = windowData.datasets.map(dataset => {
                const colors = this.getDatasetColors(dataset.label);
                
                return {
                    label: dataset.label,
                    data: dataset.data,
                    backgroundColor: colors.backgroundColor,
                    borderColor: colors.borderColor,
                    pointBackgroundColor: colors.pointBackgroundColor,
                    pointBorderColor: '#ffffff',
                    fill: true
                };
            });
            
            return {
                labels: windowData.labels,
                datasets: processedDatasets
            };
        },

        ...mapGetters({
            resultsAnnualChart: `${HAPPINESS_M}${GET_RESULTS_ANNUAL_CHART}`,
            loading: [`${HAPPINESS_M}${GET_STATE_LOADING}`],
        }),
    },
};
</script>
  
<style lang="css" scoped>
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-family: 'Amsi Pro', sans-serif;
        color: #2D0049;
        font-weight: normal;
    }

    .dates {
        display: inline-flex;
        gap: 20px;
    }

    .chart-container {
        margin: auto;
        font-family: 'Arial', sans-serif;
        padding: 20px;
    }

    .chart-wrapper {
        position: relative;
        padding: 20px;
        text-align: center;
    }

    .no-data-message {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
    }

    .no-data-message p {
        font-size: 16px;
        color: #6c757d;
        margin: 0;
    }

    /* Estilos para la lista de años */
    .scrollable-list {
        overflow-y: auto;
    }

    .year-list-item:hover {
        background-color: #f5f5f5;
        cursor: pointer;
    }

    .year-list-item.v-list-item--active {
        background-color: #6E17A5;
        color: white;
    }

    .year-list-item.v-list-item--active .v-list-item__title {
        color: white;
    }

    /* Responsive */
    @media (max-width: 600px) {        
        .chart-container {
            padding: 15px;
        }
        
        .chart-wrapper {
            padding: 10px;
        }

        .no-data-message p {
            font-size: 14px;
        }
    }
</style>